import { useEffect, useState } from 'react';
import { ArrowRight, Bot, CheckCircle2, Code2, Mail, MessagesSquare, Phone, TrendingUp, Wand2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/hooks/use-auth';
import { cn } from '@/lib/utils';
import { API_ORIGIN, publicApi } from '@/services/api';
import { AIPA_APP_ORIGIN, FEATURES, isMarketingOnlyDomain, MarketingFooter, MarketingHeader } from '@/modules/marketing/components/site-chrome';
import { PRODUCTS } from '@/modules/marketing/components/product-visuals';
import { usePlatformBranding } from '@/modules/marketing/hooks/use-platform-branding';
import { usePageSeo } from '@/modules/marketing/hooks/use-page-seo';

const DEFAULT_HOME_SEO = {
    title: 'AI Chatbot, Pricing Intelligence & CRM for Lead Capture',
    description:
        'AIPA combines an AI chat assistant, real-time pricing intelligence, and a built-in CRM to capture, qualify, and convert website visitors into booked leads — 24/7, no developer required.',
    keywords:
        'AI chatbot for lead capture, AI powered CRM, pricing intelligence software, competitor pricing analysis, conversational AI platform, website chat widget, senior living software, real estate AI chatbot, sentiment analysis software',
};

const STEPS = [
    {
        icon: Wand2,
        title: 'Set up your assistant',
        description: 'Pick a starting template for your industry, add your own knowledge base, and tune its personality.',
    },
    {
        icon: Code2,
        title: 'Brand it and embed one script tag',
        description: 'Set your logo and colors, publish, then paste a single line onto your website — no developer required.',
    },
    {
        icon: TrendingUp,
        title: 'Watch qualified leads arrive',
        description: 'Every visitor conversation becomes a scored lead in your dashboard, ready for follow-up.',
    },
];

const DEMO_WIDGET_SCRIPT_ID = 'aipa-demo-widget-script';
const DEMO_WIDGET_HOST_ID = 'aipa-widget-host';

/**
 * Embeds the exact same widget-loader.js script real tenants use on their own
 * sites (see backend/public/widget-loader.js) for whichever AiAssistant is
 * flagged is_demo — the home page becomes just another (self-referential)
 * embed site rather than a separate demo implementation. Guards against
 * double-injection (React StrictMode double-invokes effects in dev) by
 * checking for the script tag before adding it, and tears down both the
 * script and the floating-bubble host element it creates on unmount.
 */
function useDemoWidget(enabled: boolean) {
    useEffect(() => {
        if (!enabled) return;

        let cancelled = false;

        publicApi
            .get<{ success: boolean; data: { assistant_id: string } }>('/widget/demo')
            .then(({ data }) => {
                if (cancelled || document.getElementById(DEMO_WIDGET_SCRIPT_ID)) return;

                const script = document.createElement('script');
                script.id = DEMO_WIDGET_SCRIPT_ID;
                // Cache-busted: this is our own live demo of a script that's under
                // active iteration, so it must always run today's version — real
                // tenant embeds rely on normal HTTP caching (see nginx's
                // no-cache/must-revalidate rule for this file) instead.
                script.src = `${API_ORIGIN}/widget-loader.js?t=${Date.now()}`;
                script.dataset.assistantId = data.data.assistant_id;
                script.async = true;
                document.body.appendChild(script);
            })
            .catch(() => {
                // No demo assistant configured, or the request failed — the home
                // page works fine without the live widget, so fail silently.
            });

        return () => {
            cancelled = true;
            document.getElementById(DEMO_WIDGET_SCRIPT_ID)?.remove();
            document.getElementById(DEMO_WIDGET_HOST_ID)?.remove();
        };
    }, [enabled]);
}

/** Per-tab mockups for FeatureShowcase — small, self-contained, consistent card framing so swapping tabs never jumps in size. */
function AiAssistantVisual() {
    return (
        <div className="mx-auto w-72 rounded-2xl border bg-card shadow-2xl">
            <div className="flex items-center gap-2 rounded-t-2xl bg-primary px-4 py-3 text-primary-foreground">
                <div className="flex size-7 items-center justify-center rounded-full bg-white/20">
                    <Bot className="size-4" />
                </div>
                <div>
                    <span className="block text-sm font-semibold leading-tight">AI Power Assistant</span>
                    <span className="block text-[10px] leading-tight text-primary-foreground/80">Online now</span>
                </div>
                <span className="ml-auto flex size-2 rounded-full bg-emerald-400" />
            </div>
            <div className="space-y-2 p-3">
                <div className="max-w-[85%] rounded-lg rounded-tl-sm bg-muted px-3 py-2 text-xs">
                    Hi! Are you looking to buy, rent, or need property management?
                </div>
                <div className="ml-auto max-w-[75%] rounded-lg rounded-tr-sm bg-primary px-3 py-2 text-xs text-primary-foreground">Buy</div>
                <div className="max-w-[85%] rounded-lg rounded-tl-sm bg-muted px-3 py-2 text-xs">
                    Great — what city are you interested in?
                </div>
            </div>
        </div>
    );
}

function GuidedQuestionsVisual() {
    return (
        <div className="mx-auto w-72 space-y-2 rounded-2xl border bg-card p-4 shadow-2xl">
            <div className="rounded-lg border bg-muted/40 px-3 py-2 text-xs font-medium">What are you looking for?</div>
            <div className="ml-4 flex flex-col items-start gap-1.5 border-l-2 border-primary/30 pl-3">
                <span className="rounded-md bg-primary px-3 py-1.5 text-xs text-primary-foreground">A new home</span>
                <span className="rounded-md border px-3 py-1.5 text-xs text-muted-foreground">Property management</span>
            </div>
            <div className="ml-4 rounded-lg border bg-muted/40 px-3 py-2 text-xs font-medium">What&rsquo;s your budget?</div>
        </div>
    );
}

function PricingIntelligenceVisual() {
    const bars = [42, 65, 50, 80, 60];

    return (
        <div className="mx-auto w-72 rounded-2xl border bg-card p-4 shadow-2xl">
            <p className="mb-3 text-xs font-medium text-muted-foreground">Market rate vs. yours</p>
            <div className="flex h-24 items-end gap-2">
                {bars.map((height, index) => (
                    <div
                        key={index}
                        className="flex-1 rounded-t-sm bg-gradient-to-t from-primary/40 to-primary"
                        style={{ height: `${height}%` }}
                    />
                ))}
            </div>
            <div className="mt-3 rounded-lg border bg-background p-2 text-center text-xs font-semibold text-emerald-600">
                +12% above market
            </div>
        </div>
    );
}

function CrmLeadsVisual() {
    const leads = [
        { name: 'Jordan Taylor', tag: 'Hot', color: 'bg-emerald-500' },
        { name: 'Sam Rivera', tag: 'New', color: 'bg-sky-500' },
        { name: 'Casey Morgan', tag: 'Follow-up', color: 'bg-amber-500' },
    ];

    return (
        <div className="mx-auto w-72 rounded-2xl border bg-card p-4 shadow-2xl">
            <p className="mb-3 text-xs font-medium text-muted-foreground">Recent leads</p>
            <div className="space-y-2">
                {leads.map((lead) => (
                    <div key={lead.name} className="flex items-center justify-between rounded-lg border bg-background px-3 py-2 text-xs">
                        <span>{lead.name}</span>
                        <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium text-white ${lead.color}`}>{lead.tag}</span>
                    </div>
                ))}
            </div>
        </div>
    );
}

function AppointmentsVisual() {
    return (
        <div className="mx-auto w-72 rounded-2xl border bg-card p-4 shadow-2xl">
            <p className="mb-3 text-xs font-medium text-muted-foreground">Schedule a visit</p>
            <div className="grid grid-cols-7 gap-1 text-center text-[10px] text-muted-foreground">
                {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d, i) => (
                    <span key={i}>{d}</span>
                ))}
                {Array.from({ length: 14 }).map((_, i) => (
                    <span key={i} className={`rounded py-1 ${i === 9 ? 'bg-primary font-semibold text-primary-foreground' : ''}`}>
                        {i + 1}
                    </span>
                ))}
            </div>
            <div className="mt-3 space-y-1.5">
                {['10:00 AM', '2:30 PM'].map((t) => (
                    <div key={t} className="rounded-lg border bg-background px-3 py-1.5 text-xs">
                        {t}
                    </div>
                ))}
            </div>
        </div>
    );
}

function MultiChannelVisual() {
    const channels = [
        { icon: MessagesSquare, label: 'Web chat' },
        { icon: Phone, label: 'Voice' },
        { icon: Mail, label: 'Email' },
    ];

    return (
        <div className="mx-auto w-72 rounded-2xl border bg-card p-4 shadow-2xl">
            <p className="mb-3 text-xs font-medium text-muted-foreground">One assistant, every channel</p>
            <div className="space-y-2">
                {channels.map((c) => (
                    <div key={c.label} className="flex items-center gap-3 rounded-lg border bg-background px-3 py-2">
                        <span className="flex size-7 items-center justify-center rounded-full bg-primary/10 text-primary">
                            <c.icon className="size-3.5" />
                        </span>
                        <span className="text-xs font-medium">{c.label}</span>
                        <span className="ml-auto flex size-1.5 rounded-full bg-emerald-500" />
                    </div>
                ))}
            </div>
        </div>
    );
}

/** The top-level "what is AIPA" pitch — three flagship products, each funneling straight to Book a Demo. */
function ProductsSection() {
    return (
        <section id="products" className="scroll-mt-16 border-t bg-muted/20 py-20">
            <div className="mx-auto max-w-6xl px-6">
                <h2 className="text-center text-3xl font-extrabold tracking-tight">One platform, three powerful products</h2>
                <p className="mx-auto mt-3 max-w-2xl text-center text-muted-foreground">
                    AIPA brings AI-driven engagement, pricing intelligence, and reputation management together in one workspace — see any of
                    them live in a personalized demo.
                </p>

                <div className="mt-12 grid grid-cols-1 gap-6 lg:grid-cols-3">
                    {PRODUCTS.map((p) => (
                        <div key={p.key} className="flex flex-col overflow-hidden rounded-2xl border bg-card shadow-lg">
                            <div className={cn('bg-gradient-to-br p-6 text-white', p.gradient)}>
                                <div className="flex size-10 items-center justify-center rounded-lg bg-white/15">
                                    <p.icon className="size-5" />
                                </div>
                                <p className="mt-3 text-[11px] font-bold uppercase tracking-wider opacity-70">{p.eyebrow}</p>
                                <h3 className="text-xl font-bold">{p.title}</h3>
                            </div>
                            <div className="flex flex-1 flex-col items-center gap-5 p-6">
                                {p.visual}
                                <p className="text-center text-sm text-muted-foreground">{p.description}</p>
                                <ul className="w-full space-y-2">
                                    {p.bullets.map((b) => (
                                        <li key={b} className="flex items-start gap-2 text-xs">
                                            <CheckCircle2 className="mt-0.5 size-3.5 shrink-0 text-emerald-500" />
                                            {b}
                                        </li>
                                    ))}
                                </ul>
                                <Button className="mt-auto w-full" asChild>
                                    <Link to="/company/book-a-demo">Book a Demo</Link>
                                </Button>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}

const SHOWCASE_VISUALS: Record<string, () => JSX.Element> = {
    'ai-assistant': AiAssistantVisual,
    'guided-questions': GuidedQuestionsVisual,
    'pricing-intelligence': PricingIntelligenceVisual,
    'crm-leads': CrmLeadsVisual,
    appointments: AppointmentsVisual,
    'multi-channel': MultiChannelVisual,
};

/** Tabbed capability showcase — pill tabs switch a two-column panel (mockup + description) below, sitting right under the hero CTA. */
function FeatureShowcase() {
    const [active, setActive] = useState(FEATURES[0].slug);
    const feature = FEATURES.find((f) => f.slug === active) ?? FEATURES[0];
    const Visual = SHOWCASE_VISUALS[feature.slug] ?? AiAssistantVisual;

    return (
        <section id="industries" className="scroll-mt-16 pb-20">
            <div className="mx-auto max-w-5xl px-6">
                <h2 className="text-center text-3xl font-extrabold tracking-tight">AI tools for every step of the journey</h2>
                <p className="mx-auto mt-3 max-w-xl text-center text-muted-foreground">
                    AIPA&rsquo;s AI-powered tools engage every visitor with the right response at the right time, from first click to signed
                    lead.
                </p>

                <div className="mt-8 flex flex-wrap items-center justify-center gap-2">
                    {FEATURES.map((f) => (
                        <button
                            key={f.slug}
                            type="button"
                            onClick={() => setActive(f.slug)}
                            className={cn(
                                'rounded-full px-4 py-2 text-sm font-medium transition-colors',
                                f.slug === active ? 'bg-primary text-primary-foreground shadow' : 'text-muted-foreground hover:bg-muted',
                            )}
                        >
                            {f.title}
                        </button>
                    ))}
                </div>

                <div className="mt-10 grid grid-cols-1 items-center gap-10 rounded-2xl border bg-muted/20 p-8 lg:grid-cols-2 lg:p-12">
                    <Visual />
                    <div>
                        <div className="flex size-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
                            <feature.icon className="size-5" />
                        </div>
                        <h3 className="mt-4 text-2xl font-bold tracking-tight">{feature.title}</h3>
                        <p className="mt-3 text-muted-foreground">{feature.description}</p>
                        <Button className="mt-6" variant="outline" asChild>
                            <Link to="/company/book-a-demo">Book a Demo</Link>
                        </Button>
                    </div>
                </div>
            </div>
        </section>
    );
}

export function LandingPage() {
    const { user, isAuthenticated, isLoading } = useAuth();
    const { data: branding } = usePlatformBranding();

    useDemoWidget(!isLoading);
    usePageSeo({
        title: branding?.seo_title || DEFAULT_HOME_SEO.title,
        description: branding?.seo_description || DEFAULT_HOME_SEO.description,
        keywords: branding?.seo_keywords || DEFAULT_HOME_SEO.keywords,
    });

    const dashboardHref = user?.is_platform_admin ? '/admin' : '/dashboard';

    return (
        <div className="min-h-screen bg-background text-foreground">
            <MarketingHeader />

            <section className="mx-auto max-w-4xl px-6 pb-16 pt-10 text-center lg:pt-14">
                <h1 className="text-5xl font-extrabold leading-[1.08] tracking-tight text-foreground sm:text-6xl">
                    Turn website visitors into
                    <br className="hidden sm:block" />
                    qualified leads, automatically
                </h1>
                <p className="mx-auto mt-6 max-w-2xl text-lg text-muted-foreground">
                    AIPA brings together an AI Assistant, Pricing Assistant, and Sentiment Analysis in one platform — so every visitor gets
                    a guided answer, every rate is market-aware, and every review gets heard.
                </p>
                <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
                    {!isLoading && isAuthenticated ? (
                        <Button size="lg" asChild>
                            <Link to={dashboardHref}>
                                Go to your dashboard <ArrowRight className="ml-1 size-4" />
                            </Link>
                        </Button>
                    ) : isMarketingOnlyDomain() ? (
                        <Button size="lg" asChild>
                            <a href={`${AIPA_APP_ORIGIN}/login`}>
                                Log in to your workspace <ArrowRight className="ml-1 size-4" />
                            </a>
                        </Button>
                    ) : (
                        <Button size="lg" asChild>
                            <Link to="/login">
                                Log in to your workspace <ArrowRight className="ml-1 size-4" />
                            </Link>
                        </Button>
                    )}
                    <Button
                        size="lg"
                        variant="outline"
                        className="border-primary text-primary hover:bg-primary/5 hover:text-primary"
                        asChild
                    >
                        <Link to="/company/book-a-demo">Book a demo</Link>
                    </Button>
                </div>
                <p className="mt-4 text-xs text-muted-foreground">
                    Try the live chat bubble in the bottom-right corner — it&rsquo;s a real AIPA assistant, running on this page.
                </p>
            </section>

            <ProductsSection />

            <FeatureShowcase />

            <section
                id="how-it-works"
                className="scroll-mt-16 bg-gradient-to-br from-[#0f172a] via-[#1e3a5f] to-[#1d4ed8] py-24 text-white"
            >
                <div className="mx-auto max-w-6xl px-6">
                    <p className="text-center text-xs font-bold uppercase tracking-[0.2em] text-blue-300">Get started in minutes</p>
                    <h2 className="mt-3 text-center text-3xl font-extrabold tracking-tight sm:text-4xl">How it works</h2>
                    <p className="mx-auto mt-3 max-w-xl text-center text-white/70">
                        From first setup to your first booked lead — no developer, no long onboarding.
                    </p>

                    <div className="relative mt-14 grid grid-cols-1 gap-10 sm:grid-cols-3">
                        <div
                            className="absolute inset-x-0 top-8 hidden h-px bg-gradient-to-r from-transparent via-white/25 to-transparent sm:block"
                            aria-hidden="true"
                        />
                        {STEPS.map((step, index) => (
                            <div key={step.title} className="relative text-center">
                                <div className="relative z-[1] mx-auto flex size-16 items-center justify-center rounded-2xl bg-white/10 ring-1 ring-white/20 backdrop-blur">
                                    <step.icon className="size-7 text-white" />
                                    <span className="absolute -right-2 -top-2 flex size-6 items-center justify-center rounded-full bg-blue-500 text-xs font-bold shadow-lg">
                                        {index + 1}
                                    </span>
                                </div>
                                <h3 className="mt-5 font-semibold">{step.title}</h3>
                                <p className="mt-2 text-sm text-white/70">{step.description}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            <section className="border-t bg-primary text-primary-foreground">
                <div className="mx-auto flex max-w-4xl flex-col items-center gap-6 px-6 py-16 text-center">
                    <CheckCircle2 className="size-10" />
                    <h2 className="text-2xl font-semibold tracking-tight">Ready to see it in action?</h2>
                    <p className="max-w-xl text-primary-foreground/90">
                        Sign in to your workspace to configure your assistant, review leads, and manage pricing — all from one dashboard.
                    </p>
                    {!isLoading && isAuthenticated ? (
                        <Button size="lg" variant="secondary" asChild>
                            <Link to={dashboardHref}>Go to your dashboard</Link>
                        </Button>
                    ) : isMarketingOnlyDomain() ? (
                        <Button size="lg" variant="secondary" asChild>
                            <a href={`${AIPA_APP_ORIGIN}/login`}>Log in</a>
                        </Button>
                    ) : (
                        <Button size="lg" variant="secondary" asChild>
                            <Link to="/login">Log in</Link>
                        </Button>
                    )}
                </div>
            </section>

            <MarketingFooter />
        </div>
    );
}
